/**
 * Het contract van de opname-engine.
 *
 * `TemplatePackage` is exact het document dat
 * `GET /api/v1/template-versions/{version}/package` teruggeeft: alle vragen,
 * opties, knopen, items, randen en kostengroepen in één onveranderlijk pakket.
 * De PHP-engine leest dezelfde velden onder dezelfde namen.
 *
 * Regels en verificatie: `smartkeur-api/docs/legacy/graph-semantics.md`.
 */

/** Vraag uit de vragenbank (`questions.id`). */
export type QuestionId = number;
/** Antwoordoptie van een vraag (`question_options.id`). */
export type QuestionOptionId = number;
/** Knoop uit de template (`nodes.id`). */
export type TemplateNodeId = number;
/** Item binnen een knoop (`node_items.id`). */
export type NodeItemId = number;
/** Rand binnen een knoop (`node_item_options.id`). */
export type NodeItemOptionId = number;
/** Kostengroep (`cost_groups.id`). */
export type CostGroupId = number;

/**
 * Het bedrag dat de inspecteur zelf invult, per urgentie, bij een keuze zonder
 * vast bedrag (`bedraginvullen` in de bron, `amount_prompt` in het pakket).
 */
export type AmountPrompt = 'direct' | 'term' | 'improvement';

/**
 * Identiteit van een knoop in de *runtime*-boom. Voor een gewone knoop is dat
 * het knoop-id als string; binnen een multi-instantie `"{nodeId}#{scope}"`.
 * Zie `runtimeNodeId()` in `keys.ts`.
 */
export type NodeId = string;

/**
 * Sleutel van één antwoord. `"412"` voor een gewone vraag, `"412@178.0"` voor
 * dezelfde vraag binnen instantie 0 van multi-container 178, en `"m@178"` voor
 * de synthetische aantalsvraag van die container. Zie `keys.ts`.
 */
export type AnswerKey = string;

/**
 * Multi-bereik: leeg buiten elke multi-container, anders `"{containerNodeId}.{index}"`
 * met een 0-based index, genest samengevoegd met `/`.
 */
export type MultiScope = string;

export type QuestionType =
    | 'options'
    | 'options_single_multi'
    | 'options_multi'
    | 'yes_no'
    | 'number'
    | 'text'
    | 'text_items';

export type PhotoRequirement = 'none' | 'optional' | 'required';

export type QuestionSpecial = 'none' | 'build_year';

/**
 * De conditiewaarde van een antwoordoptie. Geen numerieke schaal maar een enum
 * met een eigen ernstvolgorde, zie `CONDITION_RESULT_SEVERITY_ORDER`.
 */
export type ConditionResultCode = -5 | -4 | -3 | -2 | -1 | 0 | 1 | 2 | 4 | 6 | 8;

/** `null` = geen oordeel. */
export type ConditionResult = ConditionResultCode | null;

/**
 * De volgorde waarin conditiewaarden elkaar verslaan: de eerste die voorkomt wint.
 * Geverifieerd op 455/455 knopen van dossier 91652.
 */
export const CONDITION_RESULT_SEVERITY_ORDER: readonly ConditionResultCode[] = [
    -5, -4, -3, 1, 2, 4, 6, 8, 0, -1, -2,
];

/** De kwaliteitsschaal waarop `-1` ("uit subdiagrammen") wordt afgerond. */
export const CONDITION_RESULT_SCALE: readonly ConditionResultCode[] = [1, 2, 4, 6, 8];

/** `-1` delegeert het oordeel aan de kinderen. */
export const CONDITION_RESULT_FROM_CHILDREN: ConditionResultCode = -1;

/** `-4` ("nader onderzoek") bubbelt niet omhoog naar de ouder. */
export const CONDITION_RESULT_NO_BUBBLE: ConditionResultCode = -4;

const CONDITION_RESULT_LABELS = new Map<ConditionResultCode, string>([
    [8, 'Goed'],
    [6, 'Voldoende'],
    [4, 'Matig'],
    [2, 'Onvoldoende'],
    [1, 'Actie noodzakelijk'],
    [0, 'Geen oordeel'],
    [-1, 'Uit subdiagrammen'],
    [-2, 'Geen resultaat'],
    [-3, 'Niet waarneembaar'],
    [-4, 'Nader onderzoek'],
    [-5, 'Geen beoordeling'],
]);

/** Rapportlabel bij een conditiewaarde (1-op-1, 101/101 geverifieerd). */
export function conditionResultLabel(result: ConditionResult): string {
    if (result === null) {
        return 'Geen oordeel';
    }

    return CONDITION_RESULT_LABELS.get(result) ?? 'Geen oordeel';
}

export interface PackageQuestionOption {
    id: QuestionOptionId;
    legacy_id: number | null;
    label: string;
    sort_order: number;
    /** Uit `uniek`: aankruisen wist alle andere selecties (UI-regel). */
    is_exclusive: boolean;
}

export interface PackageQuestion {
    id: QuestionId;
    legacy_id: number | null;
    type: QuestionType;
    label: string;
    help_text: string | null;
    photo_requirement: PhotoRequirement;
    photo_min: number | null;
    number_min: number | null;
    number_max: number | null;
    number_unit: string | null;
    number_decimals: number | null;
    special: QuestionSpecial;
    /** Uit `max1xvragen`: de vraag wordt hooguit één keer gesteld. */
    ask_once: boolean;
    sort_order: number;
    options: PackageQuestionOption[];
}

export interface PackageNodeItemOption {
    id: NodeItemOptionId;
    question_option_id: QuestionOptionId;
    result: ConditionResultCode | null;
    /** Verwijst naar een `node_items.id` van dezelfde knoop; `null` = einde van de tak. */
    next_item_id: NodeItemId | null;
    report_text: string | null;
    /** Bedragen als decimale string met twee decimalen, bv. `"1450.00"`. */
    direct_cost: string;
    term_cost: string;
    improvement_cost: string;
    cost_group_id: CostGroupId | null;
    /**
     * Bij import afgeleid: het schone label, anders optie 1. De sweep leest dit niet;
     * die vult alleen een schoon label dat deze rand in orde noemt (`sweepAnswerFor`).
     */
    is_clean: boolean;
    sort_order: number;
    /**
     * Deze keuze heeft geen vast bedrag: de inspecteur vult het zelf in, voor deze
     * urgentie. Wat hij invult is een eigen bedrag op het antwoord (`costs.ts`); de
     * engine rekent er niet anders door. Afwezig in pakketten van vóór dit veld.
     */
    amount_prompt?: AmountPrompt | null;
}

export interface PackageNodeItem {
    id: NodeItemId;
    legacy_id: number | null;
    question_id: QuestionId;
    /** De ingang van de knoop. */
    is_start: boolean;
    sort_order: number;
    options: PackageNodeItemOption[];
}

export interface PackageNode {
    id: TemplateNodeId;
    parent_id: TemplateNodeId | null;
    legacy_id: number | null;
    name: string;
    sort_order: number;
    nhg_code: string | null;
    /** Container: krijgt per instantie een gekloonde subboom en komt zelf niet in het rapport. */
    is_multi: boolean;
    multi_question_label: string | null;
    multi_instance_label: string | null;
    multi_report_text: string | null;
    in_report_by_default: boolean;
    items: PackageNodeItem[];
}

export interface PackageCostGroup {
    id: CostGroupId;
    code: string | null;
    name: string;
    long_name: string | null;
    sort_order: number;
}

/**
 * Het offline pakket. Onveranderlijk: een lopende opname verschuift nooit van versie.
 *
 * `section_path`, `report_section_path` en `level` staan er bewust *niet* in —
 * die worden door de engine afgeleid, omdat multi-instanties de nummering
 * verschuiven en de runtime-boom dus een andere nummering heeft dan de template.
 */
export interface TemplatePackage {
    template_version_id: number;
    template_id: number;
    version: number;
    name: string;
    /** Code van het keuringstype, bv. `nta8060`. */
    inspection_type: string;
    checksum: string;
    generated_at: string;
    cost_groups: PackageCostGroup[];
    questions: PackageQuestion[];
    nodes: PackageNode[];
}

/**
 * Eén antwoord. De velden spiegelen de kolommen van `inspection_answers`;
 * per vraagtype is er precies één veld van belang:
 *
 * | type | veld |
 * |---|---|
 * | `options`, `yes_no` | `option_ids` met exact één id |
 * | `options_multi`, `options_single_multi` | `option_ids` met alle gekozen id's |
 * | `number` | `value_number` (tevens de hoeveelheid voor de kosten, met decimalen) |
 * | `text` | `value_text` |
 * | `text_items` | `value_items` (of `value_text`) |
 * | multi-aantalsvraag | `value_number` |
 *
 * Los van het type kan elk antwoord eigen bedragen van de inspecteur dragen
 * (`direct_cost`, `term_cost`, `improvement_cost`), zie `costs.ts`.
 */
export interface Answer {
    option_ids?: readonly QuestionOptionId[] | null;
    value_number?: number | null;
    value_text?: string | null;
    value_items?: readonly string[] | null;
    /**
     * Eigen bedragen als decimale string (`"1450.00"`). `null` of afwezig =
     * niet aangepast, dan telt het berekende bedrag; `"0.00"` is een echte
     * aanpassing. Een aangepast bedrag vervangt voor die urgentie het berekende
     * bedrag van het hele antwoord en telt één keer.
     */
    direct_cost?: string | null;
    term_cost?: string | null;
    improvement_cost?: string | null;
}

/**
 * Alle antwoorden van één opname, gesleuteld op `AnswerKey`.
 *
 * Antwoorden zijn **gedeeld per vraag**, niet per knoop: dezelfde vraag in twee
 * knopen draagt hetzelfde antwoord (dat is exact wat het legacy `keuringvragen`
 * doet). Alleen een multi-instantie geeft een vraag een eigen sleutel.
 *
 * Behandel de set als onveranderlijk: maak bij elke wijziging een nieuw object.
 */
export type AnswerSet = Readonly<Record<AnswerKey, Answer>>;
